import {
  createLineItem,
  createChildLineItem,
  duplicateLineItem,
  createMaterialComponent,
  createOperationComponent,
  createHardwareComponent,
  isBlankLineItem,
  normalizeLineItem,
  MATERIAL_PRICE_OVERRIDE_BASES,
} from "./lineItems";
import {
  buildDefaultPricingTemplates,
  getPricingTemplateValues,
  matchesTemplateValue,
  normalizePricingTemplates,
  PRICING_TEMPLATE_FIELD_KEYS,
} from "../utils/pricingTemplates";
import {
  RAFTER_TAIL_TRIAL_MODEL,
  RAFTER_TAIL_TYPE_KEY,
} from "../components/workbench/estimate/rafterTailModel";

/**
 * The four hour fields that constitute a per-line labor override, and their
 * mirrors inside `fab_labor`. Exported so the UI never has to re-list them.
 */
export const LABOR_OVERRIDE_FIELDS = [
  "cnc_hours",
  "cut_hours",
  "fab_hours",
  "finish_hours",
];

/** direct field → the key it mirrors inside `fab_labor` */
const FAB_LABOR_MIRROR = {
  cnc_hours: "cnc_hours",
  cut_hours: "cut_hours",
  fab_hours: "fab_hours",
  finish_hours: "paint_hours",
};

// Initial state with new line_items structure and available data/loading states
export const initialState = {
  job_name: "",

  // Line Items — uses shared factory for consistent defaults
  line_items: [createLineItem()],

  /* How many times the line array has changed SHAPE this session.
     Per-line results are matched to lines by array position, so any edit that
     shifts a position (remove, duplicate, discard a draft over a blank row)
     leaves every later row showing another line's money until the debounced
     recalculation lands. Bumping this counter is how the app is told to drop
     those figures immediately. It counts position-shifting edits in this
     session, NOT anything about the job's content: it is UI bookkeeping, never
     persisted, never sent to the calculator, and it survives a job load
     unchanged (see SET_FORM_DATA / RESET_FORM). Appending a line cannot
     misalign an existing row, so ADD_LINE_ITEM deliberately does not bump it. */
  line_items_revision: 0,

  // Beta Pricing: custom global factors
  global_factors: {},
  // Fetched Data State. Catalog products are retired — the only Product path is
  // the rate-card product-type registry.
  availableProductTypes: [],
  availableMaterials: [],
  materialsLoading: false, // Start loading initially
  materialsError: null,

  // Keep other sections
  material_origination_cost: 0,
  material_financing_percent: 0,

  // The burden rate IS the labor cost per employee-hour — it is what the
  // engine bills shop hours at. The Island Millwork rate is the client
  // charge-out reference and is NOT an engine input; feeding it back through
  // overhead + profit + commission would double-bill labor. The three-way
  // selector that used to choose between them was retired 2026-08-18 (owner
  // ratified). `laborRateType` survives only for payload compatibility with
  // saved JobVersions — it is pinned to "burden" and is no longer user-editable
  // anywhere. `labor_custom_rate` likewise stays in state, unedited: a stale
  // $30/hr fallback below cost is exactly what the retirement removes.
  labor_burden_rate: 36.0,
  island_millwork_labor_rate: 90.0,
  labor_custom_rate: 30.0,
  laborRateType: "burden",
  // Admin/dev QA-replay knob. Per-job, not a settings mirror — RESET_FORM
  // and START_ESTIMATE must clear it. Null = use the ratified global rate.
  job_labor_burden_override: null,
  default_client_pricing_tier: "retail",
  client_pricing_tier: null,
  pricing_templates: buildDefaultPricingTemplates(),

  subcontractor_install_cost: 0,
  install_hours: 0,
  install_labor_rate: 50.0,
  community_labor_rate: 70.0,

  delivery_driver_burden: 25.23,
  mpg: 6.8,
  fuel_price: 5.0,
  truck_pm_per_mile: 0.1,
  round_trip_miles: 0,
  toll_charges: 0,
  estimated_delivery_hrs: 0,
  delivery_override: null,

  equipment_rental_cost: 0,
  hardware_costs: 0,
  powder_coating_costs: 0,
  fasteners_screws_costs: 0,
  misc_items: [{ description: "", cost: 0, charge: null }],

  overhead_ratio: 44.5,
  profit_margin: 17.0,
  material_sales_tax_rate: 7.0,
  commission_rate: 6.5,
  shop_commission_profit_sharing_rate: 0.0,
};

// --- Helper Functions ---
const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};
const toNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};
const toIntegerOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = parseInt(value, 10); // Ensure base 10
  return isNaN(num) ? null : num;
};

/**
 * The clearing rules shared by a top-level typed line and a child line.
 *
 * A product-type change drops the dimensions the new type does not use, any
 * sheet pinned by the previous type, and a waste choice that no longer
 * applies. A material-class change drops the pinned sheet. Leaving "custom"
 * waste drops the custom percentage. Typing an hour field marks labor
 * hand-entered. Clearing the quoted-price amount clears its basis and note
 * with it — the three fields are one unit, never partially set.
 *
 * Both `UPDATE_LINE_ITEM_FIELD` and `UPDATE_LINE_ITEM_CHILD_FIELD` call this
 * single function so a top-level line and a child line can never drift into
 * different rules. Returns `null` when `field` is not one of these rule
 * fields — the caller falls through to its own generic (type-coercion only)
 * handling.
 */
function applyLineClearingRule(item, field, value, availableProductTypes = []) {
  switch (field) {
    case "product_type_key": {
      const nextKey = value || null;
      const productType = (availableProductTypes || []).find(
        (type) => type.key === nextKey,
      );
      const requiredDims = Array.isArray(productType?.required_dimensions)
        ? productType.required_dimensions
        : [];
      const dimensionClears = {};
      ["length", "width", "height"].forEach((dim) => {
        if (requiredDims.length && !requiredDims.includes(dim)) {
          dimensionClears[dim] = "";
        }
      });
      const wasteClears =
        productType?.waste_prompt_mode === "prompt"
          ? {}
          : { waste_mode: null, waste_custom_pct: null };
      return {
        ...item,
        product_type_key: nextKey,
        product_id: null,
        material_option_key: null,
        material_option_label: null,
        material_selection_mode: "product_default",
        material_id: null,
        ...wasteClears,
        ...dimensionClears,
      };
    }
    case "material_classification":
      return {
        ...item,
        material_classification: value || "",
        material_id: null,
      };
    case "waste_mode": {
      const nextMode = value || null;
      const nextCustom = nextMode === "custom" ? item.waste_custom_pct : null;
      return { ...item, waste_mode: nextMode, waste_custom_pct: nextCustom };
    }
    case "waste_custom_pct":
      return { ...item, waste_custom_pct: toNumberOrNull(value) };
    case "cnc_hours":
    case "cut_hours":
    case "fab_hours":
    case "finish_hours": {
      // Any change to the hours re-arms the zero-labor warning: a confirmation
      // is about the numbers that were on the line when it was given, not a
      // permanent exemption for the line.
      // Fresh card-priced lines carry 0 defaults for legacy form shape. The
      // first hand edit must turn the *other* inherited zeroes into blanks;
      // otherwise deleting the one typed hour leaves an invisible all-zero
      // override instead of returning the line to the card.
      const inheritedCardHours =
        String(item.labor_source || "").toLowerCase() === "manual"
          ? {}
          : Object.fromEntries(
              LABOR_OVERRIDE_FIELDS.filter((hourField) => hourField !== field)
                .filter((hourField) => item[hourField] === 0)
                .map((hourField) => [hourField, ""]),
            );
      return {
        ...item,
        ...inheritedCardHours,
        [field]:
          value === null || value === undefined || value === ""
            ? ""
            : toNumber(value),
        labor_source: "manual",
        zero_labor_confirmed: false,
      };
    }
    case "material_price_override": {
      const processed = toNumberOrNull(value);
      if (processed === null) {
        return {
          ...item,
          material_price_override: null,
          material_price_override_basis: null,
          material_price_override_note: "",
        };
      }
      const hadAmount =
        item.material_price_override !== null &&
        item.material_price_override !== undefined &&
        item.material_price_override !== "";
      return {
        ...item,
        material_price_override: processed,
        material_price_override_basis: hadAmount
          ? item.material_price_override_basis || "per_unit"
          : "per_unit",
      };
    }
    case "material_price_override_basis":
      return {
        ...item,
        material_price_override_basis: MATERIAL_PRICE_OVERRIDE_BASES.includes(
          value,
        )
          ? value
          : "per_unit",
      };
    case "material_price_override_note":
      return {
        ...item,
        material_price_override_note: String(value ?? "").slice(0, 240),
      };
    default:
      return null;
  }
}

/**
 * Choosing Rafter Tail on a top-level line puts it on the pricing trial — every
 * new rafter-tail quote during the trial is priced that way. Choosing anything
 * else takes the model off, since it prices rafter tails only. A saved line is
 * never moved: this runs only when an estimator picks the product type.
 */
function withRafterTailTrialDefault(item) {
  if (item.product_type_key === RAFTER_TAIL_TYPE_KEY) {
    return { ...item, pricing_model: RAFTER_TAIL_TRIAL_MODEL };
  }
  if (!item.pricing_model) return item;
  const { pricing_model: _dropped, ...rest } = item;
  return rest;
}

const getPricingTemplateSeed = (state) => ({
  overhead_ratio: state.overhead_ratio,
  profit_margin: state.profit_margin,
  material_sales_tax_rate: state.material_sales_tax_rate,
  commission_rate: state.commission_rate,
  shop_commission_profit_sharing_rate:
    state.shop_commission_profit_sharing_rate,
});

const fillMissingPricingFields = (state) => {
  const template = getPricingTemplateValues(
    state.client_pricing_tier || state.default_client_pricing_tier || "retail",
    state.pricing_templates,
    getPricingTemplateSeed(state),
  );
  const nextState = { ...state };

  PRICING_TEMPLATE_FIELD_KEYS.forEach((field) => {
    if (
      nextState[field] === undefined ||
      nextState[field] === null ||
      nextState[field] === ""
    ) {
      nextState[field] = template[field];
    }
  });

  return nextState;
};

const applyPricingTierSelection = (
  state,
  nextTier,
  { forceAllFields = false } = {},
) => {
  const pricing_templates = normalizePricingTemplates(
    state.pricing_templates,
    getPricingTemplateSeed(state),
  );
  const resolvedTier = pricing_templates[nextTier]
    ? nextTier
    : state.default_client_pricing_tier || "retail";
  const previousTier =
    state.client_pricing_tier ||
    state.default_client_pricing_tier ||
    resolvedTier;
  const previousTemplate = getPricingTemplateValues(
    previousTier,
    pricing_templates,
    getPricingTemplateSeed(state),
  );
  const nextTemplate = getPricingTemplateValues(
    resolvedTier,
    pricing_templates,
    getPricingTemplateSeed(state),
  );
  const nextState = {
    ...state,
    client_pricing_tier: resolvedTier,
    pricing_templates,
  };

  PRICING_TEMPLATE_FIELD_KEYS.forEach((field) => {
    if (
      forceAllFields ||
      matchesTemplateValue(state[field], previousTemplate[field])
    ) {
      nextState[field] = nextTemplate[field];
    }
  });

  return nextState;
};

// --- Reducer ---
export function formReducer(state, action) {
  switch (action.type) {
    // --- Data Fetching Actions ---
    case "SET_MATERIALS_LOADING":
      return {
        ...state,
        materialsLoading: action.payload,
        materialsError: null,
      };
    case "SET_PRODUCT_TYPES":
      return {
        ...state,
        availableProductTypes: action.payload,
      };
    case "SET_MATERIALS":
      return {
        ...state,
        availableMaterials: action.payload,
        materialsLoading: false,
        materialsError: null,
      };
    case "SET_MATERIALS_ERROR":
      return {
        ...state,
        materialsLoading: false,
        materialsError: action.payload,
      };

    // --- Job Management Actions ---
    case "RESET_FORM":
      return applyPricingTierSelection(
        {
          ...initialState,
          availableProductTypes: state.availableProductTypes,
          availableMaterials: state.availableMaterials,
          materialsLoading: false,
          materialsError: null,
          // Counts structural edits in this SESSION, not in this job, so a
          // reset carries it rather than restarting it at 0.
          line_items_revision: state.line_items_revision || 0,
          labor_custom_rate: state.labor_custom_rate,
          labor_burden_rate: state.labor_burden_rate,
          island_millwork_labor_rate: state.island_millwork_labor_rate,
          laborRateType: state.laborRateType,
          // Per-job override dies with the job — do not carry it forward
          // the way labor_burden_rate / island_millwork_labor_rate are.
          job_labor_burden_override: null,
          default_client_pricing_tier:
            state.default_client_pricing_tier ||
            initialState.default_client_pricing_tier,
          client_pricing_tier:
            state.default_client_pricing_tier ||
            initialState.default_client_pricing_tier,
          pricing_templates: normalizePricingTemplates(
            state.pricing_templates,
            getPricingTemplateSeed(state),
          ),
        },
        state.default_client_pricing_tier ||
          initialState.default_client_pricing_tier,
        { forceAllFields: true },
      );

    case "START_ESTIMATE": {
      const {
        client_pricing_tier,
        job_name = "",
        customer_name = "",
        status = "draft",
        ...jobDetails
      } = action.payload || {};
      return applyPricingTierSelection(
        {
          ...initialState,
          ...jobDetails,
          job_name,
          customer_name,
          status,
          availableProductTypes: state.availableProductTypes,
          availableMaterials: state.availableMaterials,
          materialsLoading: false,
          materialsError: null,
          // Session bookkeeping, same as RESET_FORM. handleCreateEstimate sets
          // the new estimate's priced results right after this dispatch; a
          // drop to 0 here would read as a shape change and wipe them.
          line_items_revision: state.line_items_revision || 0,
          labor_custom_rate: state.labor_custom_rate,
          labor_burden_rate: state.labor_burden_rate,
          island_millwork_labor_rate: state.island_millwork_labor_rate,
          laborRateType: state.laborRateType,
          // After ...jobDetails so a leftover payload key cannot revive it.
          job_labor_burden_override: null,
          default_client_pricing_tier:
            state.default_client_pricing_tier ||
            initialState.default_client_pricing_tier,
          client_pricing_tier:
            client_pricing_tier ||
            state.default_client_pricing_tier ||
            initialState.default_client_pricing_tier,
          pricing_templates: normalizePricingTemplates(
            state.pricing_templates,
            getPricingTemplateSeed(state),
          ),
        },
        client_pricing_tier ||
          state.default_client_pricing_tier ||
          initialState.default_client_pricing_tier,
        { forceAllFields: true },
      );
    }

    case "SET_FORM_DATA": {
      // Replace form state with provided data (for loading jobs)
      // Ensure required arrays exist with defaults if missing
      const hydratedLineItems = (action.payload.line_items || []).map((item) =>
        normalizeLineItem(item),
      );
      return fillMissingPricingFields({
        ...action.payload,
        // Ensure misc_items exists as an array
        misc_items: action.payload.misc_items || [
          { description: "", cost: 0, charge: null },
        ],
        // Ensure line_items exists as an array
        line_items: hydratedLineItems,
        // A load is not a structural edit. The payload has no counter (it is
        // never persisted), so it has to be carried over explicitly — this
        // object does NOT spread ...state, and dropping the key to undefined
        // would read as a change and wipe the figures this load just set.
        line_items_revision: state.line_items_revision || 0,
        availableProductTypes: state.availableProductTypes,
        availableMaterials: state.availableMaterials,
        materialsLoading: false,
        materialsError: null,
        pricing_templates: state.pricing_templates,
        default_client_pricing_tier:
          state.default_client_pricing_tier ||
          initialState.default_client_pricing_tier,
        client_pricing_tier:
          action.payload.client_pricing_tier ||
          state.default_client_pricing_tier ||
          initialState.default_client_pricing_tier,
      });
    }

    case "UPDATE_PRICING_SETTINGS": {
      const nextState = {
        ...state,
        overhead_ratio: action.payload.overhead_ratio ?? state.overhead_ratio,
        profit_margin: action.payload.profit_margin ?? state.profit_margin,
        material_sales_tax_rate:
          action.payload.material_sales_tax_rate ??
          state.material_sales_tax_rate,
        commission_rate:
          action.payload.commission_rate ?? state.commission_rate,
        shop_commission_profit_sharing_rate:
          action.payload.shop_commission_profit_sharing_rate ??
          state.shop_commission_profit_sharing_rate,
        labor_custom_rate:
          action.payload.labor_custom_rate ?? state.labor_custom_rate,
        labor_burden_rate:
          action.payload.labor_burden_rate ?? state.labor_burden_rate,
        island_millwork_labor_rate:
          action.payload.island_millwork_labor_rate ??
          state.island_millwork_labor_rate,
        laborRateType: action.payload.laborRateType ?? state.laborRateType,
        default_client_pricing_tier:
          action.payload.default_client_pricing_tier ??
          state.default_client_pricing_tier ??
          initialState.default_client_pricing_tier,
        pricing_templates: normalizePricingTemplates(
          action.payload.pricing_templates,
          {
            ...getPricingTemplateSeed(state),
            ...action.payload,
          },
        ),
      };
      if (action.payload.applyToEstimate === false) {
        return nextState;
      }
      return applyPricingTierSelection(
        nextState,
        state.client_pricing_tier || nextState.default_client_pricing_tier,
        { forceAllFields: !state.client_pricing_tier },
      );
    }

    // --- General Field Update ---
    case "UPDATE_FIELD":
      if (action.field === "client_pricing_tier") {
        return applyPricingTierSelection(state, action.value);
      }
      return {
        ...state,
        [action.field]: action.value, // Store raw input value to avoid losing focus
      };
    // --- Global Factors (beta pricing) ---
    case "UPDATE_GLOBAL_FACTOR": {
      const { key, value } = action;
      return {
        ...state,
        global_factors: {
          ...state.global_factors,
          [key]: value,
        },
      };
    }
    case "REMOVE_GLOBAL_FACTOR": {
      const { key } = action;
      const gf = { ...state.global_factors };
      delete gf[key];
      return {
        ...state,
        global_factors: gf,
      };
    }

    // --- Line Items Actions ---
    case "ADD_LINE_ITEM":
      return {
        ...state,
        line_items: [...state.line_items, createLineItem()],
      };
    case "REMOVE_LINE_ITEM":
      if (state.line_items.length <= 1) return state; // Keep at least one row
      return {
        ...state,
        line_items: state.line_items.filter(
          (_, index) => index !== action.index,
        ),
        line_items_revision: (state.line_items_revision || 0) + 1,
      };
    case "UPDATE_LINE_ITEM": {
      // Block scope for constants
      const { index, field, value, checked } = action; // Get checked for checkboxes
      let processedValue;

      switch (field) {
        case "material_id":
          processedValue = toIntegerOrNull(value); // IDs should be integers or null
          break;
        case "length":
        case "width":
        case "height":
        case "additional_units":
        case "additional_cost_per_unit":
          processedValue = toNumber(value); // Dimensions/costs are numbers
          break;
        case "reinforcement_needed":
          processedValue = checked; // Use the checked property for checkboxes
          break;
        case "quantity":
          processedValue = toNumber(value, 1); // Default to 1 if invalid
          break;
        default: // e.g., additional_description
          processedValue = value;
      }

      return {
        ...state,
        line_items: state.line_items.map((item, idx) =>
          idx === index ? { ...item, [field]: processedValue } : item,
        ),
      };
    }

    // New action to update fab labor inside a line item
    case "UPDATE_LINE_ITEM_FAB_LABOR": {
      const { lineItemIndex, field, value } = action;
      const processedValue =
        typeof value === "number" ||
        (field !== "description" && !isNaN(Number(value)))
          ? toNumber(value)
          : value;

      return {
        ...state,
        line_items: state.line_items.map((item, idx) =>
          idx === lineItemIndex
            ? {
                ...item,
                fab_labor: {
                  ...item.fab_labor,
                  [field]: processedValue,
                },
              }
            : item,
        ),
      };
    }

    // --- Keep other action handlers (Fab Labor, Misc Items) ---
    // --- Fab Labor Items ---
    case "ADD_FAB_LABOR_ITEM":
      return {
        ...state,
        fab_labor_items: [
          ...state.fab_labor_items,
          {
            description: "TBD",
            cnc_hours: 0,
            cut_hours: 0,
            fab_hours: 0,
            paint_hours: 0,
          },
        ],
      };
    case "REMOVE_FAB_LABOR_ITEM":
      if (state.fab_labor_items.length <= 1) return state;
      return {
        ...state,
        fab_labor_items: state.fab_labor_items.filter(
          (_, index) => index !== action.index,
        ),
      };
    case "UPDATE_FAB_LABOR_ITEM":
      return {
        ...state,
        fab_labor_items: state.fab_labor_items.map((item, index) =>
          index === action.index
            ? {
                ...item,
                [action.field]:
                  typeof initialState.fab_labor_items[0][action.field] ===
                  "number"
                    ? toNumber(action.value)
                    : action.value,
              }
            : item,
        ),
      };

    // --- Misc Items ---
    case "ADD_MISC_ITEM":
      return {
        ...state,
        misc_items: [
          ...state.misc_items,
          { description: "", cost: 0, charge: null },
        ],
      };
    case "REMOVE_MISC_ITEM":
      if (state.misc_items.length <= 1) return state;
      return {
        ...state,
        misc_items: state.misc_items.filter(
          (_, index) => index !== action.index,
        ),
      };
    case "UPDATE_MISC_ITEM":
      return {
        ...state,
        misc_items: state.misc_items.map((item, index) =>
          index === action.index
            ? {
                ...item,
                [action.field]:
                  action.field === "charge"
                    ? toNumberOrNull(action.value)
                    : typeof initialState.misc_items[0][action.field] ===
                        "number"
                      ? toNumber(action.value)
                      : action.value,
              }
            : item,
        ),
      };

    // Handle line item field updates
    case "UPDATE_LINE_ITEM_FIELD": {
      const { index, field, value } = action;
      const current = state.line_items[index];

      // Product type, material class, waste mode, override cascade, and the
      // four hour fields all carry clearing rules — computed once, shared
      // with UPDATE_LINE_ITEM_CHILD_FIELD, see applyLineClearingRule above.
      const ruled = current
        ? applyLineClearingRule(
            current,
            field,
            value,
            state.availableProductTypes,
          )
        : null;
      if (ruled) {
        // The four hour fields also mirror into the legacy `fab_labor`
        // display object — a child line has no `fab_labor`, so this stays a
        // top-level-only step layered on top of the shared rule.
        const patched = [
          "cnc_hours",
          "cut_hours",
          "fab_hours",
          "finish_hours",
        ].includes(field)
          ? {
              ...ruled,
              fab_labor: {
                ...current.fab_labor,
                [field === "finish_hours" ? "paint_hours" : field]:
                  ruled[field],
              },
            }
          : ruled;
        return {
          ...state,
          line_items: state.line_items.map((item, idx) =>
            idx === index
              ? field === "product_type_key"
                ? withRafterTailTrialDefault(patched)
                : patched
              : item,
          ),
        };
      }

      // Process the value based on field type
      let processedValue = value;

      // Handle numeric fields
      if (field === "quantity") {
        processedValue = toNumber(value, 1); // Default to 1 if invalid
      } else if (field === "material_id") {
        processedValue = toIntegerOrNull(value);
      } else if (field === "unit_cost") {
        processedValue = toNumber(value);
      }

      // Return updated state with the processed value
      return {
        ...state,
        line_items: state.line_items.map((item, idx) =>
          idx === index ? { ...item, [field]: processedValue } : item,
        ),
      };
    }

    // --- Child line item actions (Goal 4) ---
    // A child is a line-shaped object nested one level under a typed parent.
    // ui-children owns the presentational panel; this lane owns all the state.

    case "ADD_LINE_ITEM_CHILD": {
      const { index, productTypeKey = null } = action;
      const item = state.line_items[index];
      if (!item) return state;
      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                children: [
                  ...(li.children || []),
                  createChildLineItem({ product_type_key: productTypeKey }),
                ],
              }
            : li,
        ),
      };
    }

    case "REMOVE_LINE_ITEM_CHILD": {
      const { index, childIndex } = action;
      const item = state.line_items[index];
      if (!item) return state;
      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                children: (li.children || []).filter(
                  (_, ci) => ci !== childIndex,
                ),
              }
            : li,
        ),
      };
    }

    case "UPDATE_LINE_ITEM_CHILD_FIELD": {
      const { index, childIndex, field, value } = action;
      const item = state.line_items[index];
      const child = item?.children?.[childIndex];
      if (!item || !child) return state;

      // Same shared rule the top-level line uses — a product-type change on a
      // child clears the same dimensions/material/waste a top-level change
      // would, and the override cascade behaves identically.
      const ruled = applyLineClearingRule(
        child,
        field,
        value,
        state.availableProductTypes,
      );
      let patchedChild = ruled;
      if (!patchedChild) {
        let processedValue = value;
        if (field === "quantity") {
          processedValue = toNumber(value, 1);
        } else if (field === "material_id") {
          processedValue = toIntegerOrNull(value);
        } else if (["length", "width", "height"].includes(field)) {
          processedValue = toNumber(value);
        }
        patchedChild = { ...child, [field]: processedValue };
      }

      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                children: li.children.map((c, ci) =>
                  ci === childIndex ? patchedChild : c,
                ),
              }
            : li,
        ),
      };
    }

    /**
     * Hand a line back to the shared rate card.
     *
     * This is deliberately NOT "dispatch UPDATE_LINE_ITEM_FIELD with 0 four
     * times". A typed zero is a *present* override — the backend reads it as
     * "this line really does take zero shop hours" and prices the line with no
     * labor at all. Only emptying the fields AND dropping `labor_source`
     * returns the line to the card.
     */
    case "CLEAR_LINE_ITEM_LABOR_OVERRIDE": {
      const { index, childIndex } = action;
      const item = state.line_items[index];
      if (!item) return state;

      // Back on the card there is no zero override left to confirm.
      const cleared = {
        ...item,
        labor_source: null,
        zero_labor_confirmed: false,
      };
      LABOR_OVERRIDE_FIELDS.forEach((field) => {
        cleared[field] = "";
      });
      if (item.fab_labor && typeof item.fab_labor === "object") {
        const fabLabor = { ...item.fab_labor };
        LABOR_OVERRIDE_FIELDS.forEach((field) => {
          const mirror = FAB_LABOR_MIRROR[field];
          if (mirror in fabLabor) fabLabor[mirror] = "";
        });
        cleared.fab_labor = fabLabor;
      }

      if (childIndex !== undefined) {
        const child = item.children?.[childIndex];
        if (!child) return state;
        const clearedChild = {
          ...child,
          labor_source: null,
          zero_labor_confirmed: false,
        };
        LABOR_OVERRIDE_FIELDS.forEach((field) => {
          clearedChild[field] = "";
        });
        return {
          ...state,
          line_items: state.line_items.map((li, idx) =>
            idx === index
              ? {
                  ...li,
                  children: li.children.map((current, ci) =>
                    ci === childIndex ? clearedChild : current,
                  ),
                }
              : li,
          ),
        };
      }

      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index ? cleared : li,
        ),
      };
    }

    /**
     * "Yes — this line really does take no shop time."
     *
     * The flag is presentational only: it quiets the warning on the panel, the
     * rail and the cost trace. It never reaches a number — the hours already
     * say what the line costs. Any later edit to an hour field clears it again.
     */
    case "CONFIRM_ZERO_LABOR_OVERRIDE": {
      const { index, childIndex } = action;
      const item = state.line_items[index];
      if (!item) return state;

      if (childIndex !== undefined) {
        if (!item.children?.[childIndex]) return state;
        return {
          ...state,
          line_items: state.line_items.map((li, idx) =>
            idx === index
              ? {
                  ...li,
                  children: li.children.map((child, ci) =>
                    ci === childIndex
                      ? { ...child, zero_labor_confirmed: true }
                      : child,
                  ),
                }
              : li,
          ),
        };
      }

      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index ? { ...li, zero_labor_confirmed: true } : li,
        ),
      };
    }

    // --- Smart Line Item Actions ---

    case "TOGGLE_LINE_ITEM_MODE": {
      const { index } = action;
      const item = state.line_items[index];
      if (!item) return state;
      const newMode = item.mode === "custom" ? "product" : "custom";
      const updates = { mode: newMode };
      // pricing_model rides along untouched: every trial check ignores a
      // custom line, and switching back restores the model it had — a mode
      // toggle is not a pricing-model choice.

      // Leave a new custom line empty so the estimator chooses the first cost
      // intentionally: purchased item, build material, or shop operation. The
      // old blank-material scaffold made one-offs look like assemblies and
      // could fail validation before the user discovered the remove action.

      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index ? { ...li, ...updates } : li,
        ),
      };
    }

    case "ADD_COMPONENT": {
      const { index, componentType } = action;
      const item = state.line_items[index];
      if (!item) return state;

      let newComponent;
      if (componentType === "operation") {
        newComponent = createOperationComponent();
      } else if (componentType === "hardware") {
        newComponent = createHardwareComponent();
      } else {
        newComponent = createMaterialComponent();
      }

      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                assembly_components: [
                  ...(li.assembly_components || []),
                  newComponent,
                ],
              }
            : li,
        ),
      };
    }

    case "REMOVE_COMPONENT": {
      const { index, componentIndex } = action;
      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                assembly_components: (li.assembly_components || []).filter(
                  (_, ci) => ci !== componentIndex,
                ),
              }
            : li,
        ),
      };
    }

    case "UPDATE_COMPONENT": {
      const {
        index,
        componentIndex,
        field: compField,
        value: compValue,
      } = action;
      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                assembly_components: (li.assembly_components || []).map(
                  (comp, ci) =>
                    ci === componentIndex
                      ? { ...comp, [compField]: compValue }
                      : comp,
                ),
              }
            : li,
        ),
      };
    }

    /**
     * Land an AI-drafted line in the job.
     *
     * The draft arrives as an ordinary Custom-Mode line — the composer proposes
     * inputs, not a price — so it enters state through the same normalizer as a
     * loaded JobVersion. Nothing here prices it: the results go stale exactly
     * as they would after a hand edit, and the estimator presses Calculate.
     *
     * The blank-line replacement exists because "New estimate → Draft with AI"
     * is the common path, and appending would leave an empty product row
     * sitting above the draft that nobody asked for and nobody can price.
     */
    case "INSERT_AI_DRAFT_LINE_ITEM": {
      const drafted = normalizeLineItem(action.lineItem);
      const replacesBlank =
        state.line_items.length === 1 && isBlankLineItem(state.line_items[0]);
      return {
        ...state,
        line_items: replacesBlank ? [drafted] : [...state.line_items, drafted],
        // Replacing the blank row swaps what sits at index 0; appending does
        // not move anything that already has a price.
        ...(replacesBlank
          ? { line_items_revision: (state.line_items_revision || 0) + 1 }
          : {}),
      };
    }

    /**
     * The estimator has reviewed a draft and taken ownership of it.
     *
     * Only the timestamp changes — the line is already a normal custom line and
     * was editable the whole time. The stamp is not a status flag we later
     * clear: it is the permanent record that a model proposed these inputs and
     * a human signed off on them, and it rides into the JobVersion.
     */
    case "ACCEPT_AI_DRAFT_LINE_ITEM": {
      const { index } = action;
      const item = state.line_items[index];
      if (!item?.ai_drafted || item.ai_drafted.accepted_at) return state;
      return {
        ...state,
        line_items: state.line_items.map((li, idx) =>
          idx === index
            ? {
                ...li,
                ai_drafted: {
                  ...li.ai_drafted,
                  accepted_at: new Date().toISOString(),
                },
              }
            : li,
        ),
      };
    }

    /**
     * Throw a draft away.
     *
     * REMOVE_LINE_ITEM refuses to go below one row, which would strand an
     * undiscardable AI draft on a one-line job — so the sole-line case resets
     * to a blank line instead of removing nothing at all.
     */
    case "DISCARD_AI_DRAFT_LINE_ITEM": {
      const { index } = action;
      if (!state.line_items[index]) return state;
      return {
        ...state,
        line_items:
          state.line_items.length <= 1
            ? [createLineItem()]
            : state.line_items.filter((_, idx) => idx !== index),
        line_items_revision: (state.line_items_revision || 0) + 1,
      };
    }

    case "DUPLICATE_LINE_ITEM": {
      const { index } = action;
      const item = state.line_items[index];
      if (!item) return state;
      const cloned = duplicateLineItem(item);
      const newItems = [...state.line_items];
      newItems.splice(index + 1, 0, cloned);
      return {
        ...state,
        line_items: newItems,
        line_items_revision: (state.line_items_revision || 0) + 1,
      };
    }

    default:
      console.warn(`Unhandled action type: ${action.type}`);
      return state; // Return current state if action is not handled
  }
}
